import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from './ui/icon.component';
import { AnimateDirective } from '../directives/animate.directive';

@Component({
  selector: 'app-pack-v2',
  standalone: true,
  imports: [CommonModule, IconComponent, AnimateDirective],
  template: `
    <section class="pt-16 md:pt-20 lg:pt-24 pb-8 md:pb-12 bg-[#111111] relative overflow-hidden border-t border-white/5">
      <div class="absolute inset-0 bg-[#0a0a0a]"></div>
      <div class="absolute right-0 top-0 w-1/3 h-full bg-gradient-to-l from-[#1c1c1c] to-transparent skew-x-12 opacity-50"></div>

      <div class="container mx-auto px-4 md:px-6 relative z-10">
        <div class="text-center mb-20">
          <span appAnimate class="text-[#FF5500] font-bold tracking-wider uppercase text-xs mb-3 block">Tecnologia de Ponta</span>
          <h2 appAnimate [delay]="100" class="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            Inteligência que monitora
          </h2>
          <p appAnimate [delay]="200" class="text-gray-300 max-w-3xl mx-auto text-lg md:text-xl">
            Hardware robusto e software inteligente trabalhando em sincronia para garantir sua tranquilidade.
          </p>
        </div>

        <!-- Video headline -->
        <p appAnimate [delay]="100" class="text-center text-white text-xl md:text-2xl font-bold max-w-3xl mx-auto mb-6 leading-snug">
          Veja como uma operação com 200 colaboradores eliminou 100% das autuações trabalhistas em 6 meses.
        </p>

        <!-- YouTube Lite Facade (lazy-loaded) -->
        <div appAnimate [delay]="150" class="w-full max-w-5xl mx-auto aspect-[16/9] bg-[#1c1c1c] rounded-2xl border border-white/5 relative overflow-hidden flex items-center justify-center shadow-2xl cursor-pointer group"
             (click)="loadVideo()">
          @if (videoLoaded()) {
            <iframe
              class="absolute inset-0 w-full h-full z-10"
              src="https://www.youtube.com/embed/GDAm6U5G-qQ?autoplay=1"
              title="Vídeo demonstrativo da solução beetrack para controle de pausa térmica"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowfullscreen
              loading="lazy">
            </iframe>
          } @else {
            <img
              src="https://img.youtube.com/vi/GDAm6U5G-qQ/maxresdefault.jpg"
              alt="Clique para assistir o vídeo demonstrativo da beetrack"
              class="absolute inset-0 w-full h-full object-cover z-10 transition-transform duration-500 group-hover:scale-105"
              width="1280"
              height="720"
              loading="lazy">
            <div class="absolute inset-0 z-20 flex items-center justify-center">
              <div class="w-16 h-16 md:w-20 md:h-20 bg-[#FF5500] rounded-full flex items-center justify-center shadow-2xl shadow-[#FF5500]/30 group-hover:scale-110 group-hover:bg-[#E04800] transition-all duration-300">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" class="w-7 h-7 md:w-9 md:h-9 ml-1">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </div>
            </div>
            <div class="absolute inset-0 bg-gradient-to-t from-[#111111]/60 via-transparent to-transparent z-15 pointer-events-none"></div>
          }
        </div>

        <!-- Video testimonial -->
        <p appAnimate [delay]="200" class="text-center text-gray-400 text-sm md:text-base italic max-w-2xl mx-auto mt-5 mb-16">
          "Em menos de 48h instalado, já tínhamos visibilidade total da nossa operação." — <span class="not-italic font-medium text-gray-300">Gerente de RH, frigorífico de aves, SP</span>
        </p>

        <div class="grid md:grid-cols-4 gap-6 lg:gap-8">

          <!-- Antenas -->
          <div appAnimate [delay]="200" class="h-full">
            <div class="bg-[#1c1c1c] rounded-xl shadow-lg border border-white/5 overflow-hidden flex flex-col group hover:border-[#FF5500]/30 transition-all duration-300 hover:-translate-y-1 h-full">
              <div class="h-64 bg-gray-50 relative overflow-hidden flex items-center justify-center">
                <img src="/images/gateway-3.webp"
                     alt="Gateway Bluetooth"
                     width="400"
                     height="256"
                     loading="lazy"
                     class="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105 mix-blend-darken">
                <div class="absolute inset-0 bg-gradient-to-b from-transparent to-[#1c1c1c]/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <div class="p-6 flex-1 flex flex-col">
                <h3 class="text-lg font-bold text-white mb-2">Gateways Bluetooth</h3>
                <p class="text-gray-300 text-sm leading-relaxed mb-4">
                  Monitoramento preciso de entrada e saída.
                </p>
                <div class="mt-auto pt-4 border-t border-white/5">
                  <span class="text-xs font-semibold text-[#FF5500] flex items-center gap-2">
                    <app-icon name="check-circle" [size]="14"></app-icon> Instalação Plug & Play
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Tags -->
          <div appAnimate [delay]="250" class="h-full">
            <div class="bg-[#1c1c1c] rounded-xl shadow-lg border border-white/5 overflow-hidden flex flex-col group hover:border-[#FF5500]/30 transition-all duration-300 hover:-translate-y-1 h-full">
              <div class="h-64 bg-gray-50 relative overflow-hidden flex items-center justify-center">
                <img src="/images/tag-cracha-2.webp"
                     alt="Tag Industrial"
                     width="400"
                     height="256"
                     loading="lazy"
                     class="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105 mix-blend-darken">
                <div class="absolute inset-0 bg-gradient-to-b from-transparent to-[#1c1c1c]/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <div class="p-6 flex-1 flex flex-col">
                <h3 class="text-lg font-bold text-white mb-2">Tags Industriais</h3>
                <p class="text-gray-300 text-sm leading-relaxed mb-4">
                  Dispositivos resistentes com alerta sonoro.
                </p>
                <div class="mt-auto pt-4 border-t border-white/5">
                  <span class="text-xs font-semibold text-[#FF5500] flex items-center gap-2">
                    <app-icon name="check-circle" [size]="14"></app-icon> Bateria Trocável
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Dashboards -->
          <div appAnimate [delay]="300" class="h-full">
            <div class="bg-[#1c1c1c] rounded-xl shadow-lg border border-white/5 overflow-hidden flex flex-col group hover:border-[#FF5500]/30 transition-all duration-300 hover:-translate-y-1 h-full">
              <div class="h-64 bg-gray-50 relative overflow-hidden flex items-center justify-center">
                <img src="/images/nova-dashboard-com-monitor.webp"
                     alt="Dashboard"
                     width="400"
                     height="256"
                     loading="lazy"
                     class="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105 mix-blend-darken">
                <div class="absolute inset-0 bg-gradient-to-b from-transparent to-[#1c1c1c]/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <div class="p-6 flex-1 flex flex-col">
                <h3 class="text-lg font-bold text-white mb-2">Dashboard e Telas Display</h3>
                <p class="text-gray-300 text-sm leading-relaxed mb-4">
                  Monitoramento online, com aviso nas áreas frias e descanso.
                </p>
                <div class="mt-auto pt-4 border-t border-white/5">
                  <span class="text-xs font-semibold text-[#FF5500] flex items-center gap-2">
                    <app-icon name="check-circle" [size]="14"></app-icon> 100% Web & Seguro
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Reports -->
          <div appAnimate [delay]="350" class="h-full">
            <div class="bg-[#1c1c1c] rounded-xl shadow-lg border border-white/5 overflow-hidden flex flex-col group hover:border-[#FF5500]/30 transition-all duration-300 hover:-translate-y-1 h-full">
              <div class="h-64 bg-gray-50 relative overflow-hidden flex items-center justify-center">
                <img src="/images/relatorio-2.webp"
                     alt="Relatório de advertências"
                     width="400"
                     height="256"
                     loading="lazy"
                     class="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105 mix-blend-darken">
                <div class="absolute inset-0 bg-gradient-to-b from-transparent to-[#1c1c1c]/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <div class="p-6 flex-1 flex flex-col">
                <h3 class="text-lg font-bold text-white mb-2">Relatório de Advertências</h3>
                <p class="text-gray-300 text-sm leading-relaxed mb-4">
                  Documento em PDF detalhando todas as etapas.
                </p>
                <div class="mt-auto pt-4 border-t border-white/5">
                  <span class="text-xs font-semibold text-[#FF5500] flex items-center gap-2">
                    <app-icon name="check-circle" [size]="14"></app-icon> Assinaturas do colaborador e supervisor
                  </span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  `
})
export class PackV2Component {
  videoLoaded = signal(false);

  loadVideo() {
    this.videoLoaded.set(true);
  }
}
